import type { BreakPoint } from '@bucketplace/design-system';

import type { ContentsCardLayoutType, LifeEventCardType } from '@/brand/types';

export type RebrandBreakPoint<T> = Record<BreakPoint, T>;

/** 스타일 */
export const DOCUMENTARY_SIZE: RebrandBreakPoint<{
  width: number;
  height: number;
  left: number;
  fontSize: number;
}> = {
  desktop: { width: 1040, height: 585, left: 50, fontSize: 48 },
  laptop: { width: 1040, height: 585, left: 50, fontSize: 48 },
  tablet: { width: 768, height: 570, left: 24, fontSize: 30 },
  mobile: { width: 327, height: 570, left: 24, fontSize: 30 },
};

export const BRAND_NAME_SIZE: RebrandBreakPoint<{
  BigSymbol: number;
  symbol: number;
  wordmark: number;
}> = {
  mobile: { BigSymbol: 110, symbol: 63, wordmark: 147 },
  tablet: { BigSymbol: 110, symbol: 63, wordmark: 147 },
  laptop: { BigSymbol: 140, symbol: 140, wordmark: 326 },
  desktop: { BigSymbol: 140, symbol: 140, wordmark: 326 },
};

export const LIFE_EVENT_IMAGE_SIZE: RebrandBreakPoint<{
  width: number;
  height: number;
  containerWidth: number;
  descriptionHeight?: number;
}> = {
  mobile: {
    width: 300,
    height: 350,
    containerWidth: 334,
    descriptionHeight: 100,
  },
  tablet: {
    width: 300,
    height: 350,
    containerWidth: 334,
    descriptionHeight: 100,
  },
  laptop: {
    width: 390,
    height: 520,
    containerWidth: 424,
    descriptionHeight: 140,
  },
  desktop: {
    width: 390,
    height: 520,
    containerWidth: 424,
    descriptionHeight: 140,
  },
};

export const EVENT_SECTION_IMAGE_SIZE: RebrandBreakPoint<{
  width: number;
  height: number;
}> = {
  mobile: { width: 768, height: 570 },
  tablet: { width: 768, height: 570 },
  laptop: { width: 1040, height: 520 },
  desktop: { width: 1040, height: 520 },
};

export const REBRAND_SIZE = {
  documentary: DOCUMENTARY_SIZE,
  brandName: BRAND_NAME_SIZE,
  lifeEventImage: LIFE_EVENT_IMAGE_SIZE,
  eventSectionImage: EVENT_SECTION_IMAGE_SIZE,
} as const;

export const CONTENTS_CARD_GAP = {
  mobile: 12,
  tablet: 12,
  laptop: 20,
  desktop: 20,
};

export type RebrandSizeType = keyof typeof REBRAND_SIZE;

/** 아틀라스 이미지 경로 */
export const CLOUD_SECTION_ASSETS_URL = {
  positions: {
    mobile:
      'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-418272828350528.json',
    pc: 'https://prs.ohousecdn.com/apne2/events/rebranding/pc/v1-418272176341056.json',
  },
  atlas: {
    mobile: {
      priority: [
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-418273055121536.webp',
      ],
      normal: [
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142355451968.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142357893184.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142358896768.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142360588416.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142362087552.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142362583040.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142363668480.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142364475456.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142365171712.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142366113792.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142367326272.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142367608896.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142368854016.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142370635904.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142371049472.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142371893312.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142374150208.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142374150208.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142374780992.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/mobile/v1-419142374932608.webp',
      ],
    },
    pc: {
      priority: [
        'https://prs.ohousecdn.com/apne2/events/rebranding/pc/v1-418272409403520.webp',
      ],
      normal: [
        'https://prs.ohousecdn.com/apne2/events/rebranding/pc/v1-418272411287616.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/pc/v1-418272411836544.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/pc/v1-418272414842880.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/pc/v1-418272417292352.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/pc/v1-418272417112192.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/pc/v1-418272421691456.webp',
        'https://prs.ohousecdn.com/apne2/events/rebranding/pc/v1-418272422690816.webp',
      ],
    },
  },
};

/** 심볼 이미지 경로 */
/** 미니다큐 문구1 */
export const MINI_DOCUMENTARY_TEXT1 = {
  mobile: '누구나 예쁜 집에\n살 수 있다고\n시작했던 오늘의집이',
  tablet: '누구나 예쁜 집에\n살 수 있다고\n시작했던 오늘의집이',
  laptop: '누구나 예쁜 집에 살 수 있다고\n시작했던 오늘의집이',
  desktop: '누구나 예쁜 집에 살 수 있다고\n시작했던 오늘의집이',
};

/** 미니다큐 문구2 > after */
export const MINI_DOCUMENTARY_TEXT2 = {
  mobile: '로\n솔루션이 가득찬\n집이 됩니다',
  tablet: '로\n솔루션이 가득찬\n집이 됩니다',
  laptop: '로\n솔루션이 가득찬 집이 됩니다',
  desktop: '로\n솔루션이 가득찬 집이 됩니다',
};

/** 라이프이벤트 텍스트 */
export const LIFE_EVENT_TEXT = [
  '영감을 주는 딱 맞는 콘텐츠,',
  '내 맘대로 담는 최저가 패키지,',
  '투명한 견적, 검증된 시공 업체,',
  '시공 하자까지 책임 보장으로',
  '집의 변화가 쉬워집니다',
];

/** 라이프이벤트 이미지 */
export const LIFE_EVENT_IMAGES: LifeEventCardType[] = [
  {
    text: '집 크기 상관없이\n답을 주는 콘텐츠',
    url: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424838821789696.mp4',
    thumbnail:
      'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424839480959040.mp4',
    rotate: 0,
  },
  {
    text: '브랜드 고민없이\n내가 만드는 패키지',
    url: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424838821806208.mp4',
    thumbnail:
      'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424839480725632.mp4',
    rotate: 5,
  },
  {
    text: '첫 시공도 문제없이\n투명한 표준 견적서',
    url: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424838821810240.mp4',
    thumbnail:
      'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424839480348736.mp4',
    rotate: -5,
  },
  {
    text: '하자 문제없이\n책임보장 서비스',
    url: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424838824972352.mp4',
    thumbnail:
      'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424839483224064.mp4',
    rotate: 7,
  },
];

export const LIFE_EVENT_IMAGES_REVERSE = [...LIFE_EVENT_IMAGES].reverse();

export const CONTENTS_ITEMS: ContentsCardLayoutType[] = [
  {
    id: '1',
    items: [
      {
        id: '1-1',
        type: 'video',
        shape: 'square',
        width: {
          mobile: 267,
          tablet: 267,
          laptop: 400,
          desktop: 400,
        },
        height: {
          mobile: 392,
          tablet: 392,
          laptop: 540,
          desktop: 540,
        },
        title: '수천만의 이야기로\n실패 없이 완성한 우리 집',
        previewUrl:
          'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424838284525697.mp4',
        url: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424838284845056.mp4',
      },
    ],
  },
  {
    id: '2',
    items: [
      {
        id: '2-1',
        type: 'image',
        shape: 'circle',
        width: {
          mobile: 180,
          tablet: 180,
          laptop: 214,
          desktop: 214,
        },
        height: {
          mobile: 180,
          tablet: 180,
          laptop: 214,
          desktop: 214,
        },
        src: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298861903936.webp',
      },
      {
        id: '2-2',
        type: 'image',
        shape: 'square',
        width: {
          mobile: 180,
          tablet: 180,
          laptop: 214,
          desktop: 214,
        },
        height: {
          mobile: 200,
          tablet: 200,
          laptop: 306,
          desktop: 306,
        },
        src: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298864250881.webp',
      },
    ],
  },
  {
    id: '3',
    justify: 'flex-end',
    items: [
      {
        id: '3-1',
        type: 'contents',
        shape: 'square',
        width: {
          mobile: 240,
          tablet: 240,
          laptop: 350,
          desktop: 350,
        },
        height: {
          mobile: 200,
          tablet: 200,
          laptop: 306,
          desktop: 306,
        },
        title: '투명한 견적으로\n안심하고 맡길 수 있는 업체',
        pcText:
          '집은 가족이 성장하고,\n함께 나이 들어가는 세월을 담은 공간이죠.\n그 시간을 위해 준비해야 할 것들을 먼저\n살피고, 집이 제 역할을 할 수 있도록\n고민합니다.',
        mobileText:
          '집은 가족이 성장하고, 함께 나이\n들어가는 세월을 담은 공간이죠.\n그 시간을 위해 준비해야 할 것들을\n먼저 살피고, 집이 제 역할을 할 수\n 있도록 고민합니다.',
        titleAlign: 'center',
        writer: 'by 커먼그라운드 대표',
        imageUrl:
          'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298864250880.webp',
      },
    ],
  },
  {
    id: '4',
    items: [
      {
        id: '4-1',
        type: 'video',
        shape: 'square',
        width: {
          mobile: 267,
          tablet: 267,
          laptop: 400,
          desktop: 400,
        },
        height: {
          mobile: 392,
          tablet: 392,
          laptop: 540,
          desktop: 540,
        },
        title: '꿈꾸던 모든 것을 채워가는\n취향 가득한 이야기',
        previewUrl:
          'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424838284525696.mp4',
        url: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424838287560832.mp4',
      },
    ],
  },
  {
    id: '5',
    justify: 'flex-start',
    items: [
      {
        id: '5-1',
        type: 'contents',
        shape: 'square',
        width: {
          mobile: 248,
          tablet: 248,
          laptop: 380,
          desktop: 380,
        },
        height: {
          mobile: 208,
          tablet: 208,
          laptop: 314,
          desktop: 314,
        },
        title: '건축, 시공, 홈스타일링까지\n하나하나 만들어 나가며',
        pcText:
          '집은 이불 같아요.\n힘들고 지쳐도 그 속에 있음 편안해지잖아요.\n직접 짓는다는 게 쉬운 일만은 아니었지만\n잊지 못할 경험이었고\n지금은 무척 만족스러워요.',
        mobileText:
          '집은 이불 같아요. 힘들고 지쳐도\n그 속에 있음 편안해지잖아요.\n직접 짓는다는 게 쉬운 일만은\n아니었지만 잊지 못할 경험이었고\n지금은 무척 만족스러워요.',
        writer: 'by 하율84',
        titleAlign: 'center',
        imageUrl:
          'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298861903938.webp',
      },
    ],
  },
  {
    id: '6',
    items: [
      {
        id: '6-1',
        type: 'image',
        shape: 'square',
        width: {
          mobile: 172,
          tablet: 172,
          laptop: 206,
          desktop: 206,
        },
        height: {
          mobile: 208,
          tablet: 208,
          laptop: 314,
          desktop: 314,
        },
        src: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298865741952.webp',
      },
      {
        id: '6-2',
        type: 'image',
        shape: 'circle',
        width: {
          mobile: 172,
          tablet: 172,
          laptop: 206,
          desktop: 206,
        },
        height: {
          mobile: 172,
          tablet: 172,
          laptop: 206,
          desktop: 206,
        },
        src: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298863767616.webp',
      },
    ],
  },
  {
    id: '7',
    items: [
      {
        id: '7-1',
        type: 'contents',
        shape: 'square',
        width: {
          mobile: 267,
          tablet: 267,
          laptop: 400,
          desktop: 400,
        },
        height: {
          mobile: 392,
          tablet: 392,
          laptop: 540,
          desktop: 540,
        },
        title: '나만의 취향을 채워가는\n멋진 라이프 스타일',
        pcText:
          '각자의 취향과 상황에 따라\n정답은 언제나 바뀔 수 있고,\n본인 마음에만 든다면 그것이\n어떤 인테리어든 옳은 인테리어라는 것.\n집이라는 공간은 생활 방식과 취향을\n가장 잘 담아낼 수 있는 곳이기에,\n어떻게 꾸미고 활용하느냐에 따라\n완전히 달라진다고 생각합니다.',
        mobileText:
          '각자의 취향과 상황에 따라\n정답은 언제나 바뀔 수 있고,\n본인 마음에만 든다면 그것이\n어떤 인테리어든 옳은 인테리어라는 것.\n집이라는 공간은 생활 방식과 취향을\n가장 잘 담아낼 수 있는 곳이기에,\n어떻게 꾸미고 활용하느냐에 따라\n완전히 달라진다고 생각합니다.',
        writer: 'by haus3oo',
        imageUrl:
          'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298866667648.webp',
      },
    ],
  },
  {
    id: '8',
    justify: 'flex-end',
    items: [
      {
        id: '8-1',
        type: 'contents',
        shape: 'square',
        width: {
          mobile: 267,
          tablet: 267,
          laptop: 400,
          desktop: 400,
        },
        height: {
          mobile: 280,
          tablet: 280,
          laptop: 390,
          desktop: 390,
        },
        title: '부부도, 아이도, 반려동물도\n모두가 기쁜 시간을 위해',
        titleAlign: 'center',
        pcText:
          '저희 집 ‘흔연재’는\n"집에 들어서는 순간은\n늘 기쁘고 재미있게 살자"\n라는 의미를 담고 있습니다.\n그만큼 저희 가족은\n집에 있는 지금의 순간, 마음이 기쁜지 \n늘 생각하며 살려고 노력해요.',
        mobileText:
          '저희 집 ‘흔연재’는\n"집에 들어서는 순간은\n늘 기쁘고 재미있게 살자"\n라는 의미를 담고 있습니다.\n그만큼 저희 가족은\n집에 있는 지금의 순간, 마음이 기쁜지\n늘 생각하며 살려고 노력해요.',
        writer: 'by 흔연재',
        imageUrl:
          'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298867109952.webp',
      },
    ],
  },
  {
    id: '9',
    items: [
      {
        id: '9-1',
        type: 'contents',
        shape: 'square',
        width: {
          mobile: 335,
          tablet: 335,
          laptop: 552,
          desktop: 552,
        },
        height: {
          mobile: 240,
          tablet: 240,
          laptop: 306,
          desktop: 306,
        },
        title: '살림까지 꼼꼼히 정돈하며\n일상을 잘 챙길 수 있도록',
        titleAlign: 'center',
        pcText:
          '처음부터 정리·수납에 관심이 있었던 건 아니에요.\n우리 가족을 위해 노력한 살림 생활이 루틴이 되었답니다.\n정리·수납으로 일상이 즐거운 집을 만드는 것은\n아직 현재 진행 중이고, 앞으로도 계속될 거예요.',
        mobileText:
          '처음부터 정리·수납에 관심이 있었던 건 아니에요.\n우리 가족을 위해 노력한 살림 생활이\n루틴이 되었답니다. 정리·수납으로 일상이\n즐거운 집을 만드는 것은 아직 현재 진행 중이고,\n앞으로도 계속될 거예요.',
        writer: 'by min_home_1',
        imageUrl:
          'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298865217536.webp',
      },
    ],
    childrenLayout: {
      id: '9-2',
      flexDirection: 'row',
      items: [
        {
          id: '9-2-1',
          type: 'image',
          shape: 'circle',
          width: {
            mobile: 140,
            tablet: 140,
            laptop: 214,
            desktop: 214,
          },
          height: {
            mobile: 140,
            tablet: 140,
            laptop: 214,
            desktop: 214,
          },
          src: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418309422460992.png',
        },
        {
          id: '9-2-2',
          type: 'image',
          shape: 'square',
          width: {
            mobile: 183,
            tablet: 183,
            laptop: 318,
            desktop: 318,
          },
          height: {
            mobile: 140,
            tablet: 140,
            laptop: 214,
            desktop: 214,
          },
          src: 'https://prs.ohousecdn.com/apne2/events/rebranding/v1-418298865709056.webp',
        },
      ],
    },
  },
];

/** 이벤트 섹션 */
export const EVENT_SECTION_IMAGE =
  'https://prs.ohousecdn.com/apne2/events/rebranding/v1-424836272386048.png';

/** 커뮤니티 링크 */
export const COMMUNITY_LINK =
  'https://contents.ohou.se/community/posts/424897722101760';

export const CHEVRON_DOWN_MOTION = {
  v: '5.9.0',
  fr: 30,
  ip: 0,
  op: 51,
  w: 180,
  h: 180,
  nm: '컴포지션 1',
  ddd: 0,
  assets: [],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: '레이어 1/Frame 2147203438 사본 윤곽선',
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 0, ix: 10 },
        p: {
          a: 1,
          k: [
            {
              i: { x: 0.269, y: 1 },
              o: { x: 0.481, y: 0 },
              t: 0,
              s: [90, 75, 0],
              to: [0, 0, 0],
              ti: [0, 0, 0],
            },
            {
              i: { x: 0.545, y: 1 },
              o: { x: 0.735, y: 0 },
              t: 26,
              s: [90, 120, 0],
              to: [0, 0, 0],
              ti: [0, 0, 0],
            },
            { t: 51, s: [90, 75, 0] },
          ],
          ix: 2,
          l: 2,
        },
        a: { a: 0, k: [22.5, 14.5, 0], ix: 1, l: 2 },
        s: { a: 0, k: [100, 100, 100], ix: 6, l: 2 },
      },
      ao: 0,
      shapes: [
        {
          ty: 'gr',
          it: [
            {
              ind: 0,
              ty: 'sh',
              ix: 1,
              ks: {
                a: 0,
                k: {
                  i: [
                    [0, 0],
                    [0, 0],
                    [0, 0],
                  ],
                  o: [
                    [0, 0],
                    [0, 0],
                    [0, 0],
                  ],
                  v: [
                    [17.708, -9.661],
                    [0.001, 9.661],
                    [-17.708, -9.661],
                  ],
                  c: false,
                },
                ix: 2,
              },
              nm: '패스 1',
              mn: 'ADBE Vector Shape - Group',
              hd: false,
            },
            {
              ty: 'st',
              c: {
                a: 0,
                k: [0.039215686275, 0.090196078431, 0.121568627451, 1],
                ix: 3,
              },
              o: { a: 0, k: 100, ix: 4 },
              w: { a: 0, k: 4.583, ix: 5 },
              lc: 2,
              lj: 2,
              bm: 0,
              nm: '선 1',
              mn: 'ADBE Vector Graphic - Stroke',
              hd: false,
            },
            {
              ty: 'tr',
              p: { a: 0, k: [22.291, 14.244], ix: 2 },
              a: { a: 0, k: [0, 0], ix: 1 },
              s: { a: 0, k: [300, 300], ix: 3 },
              r: { a: 0, k: 0, ix: 6 },
              o: { a: 0, k: 100, ix: 7 },
              sk: { a: 0, k: 0, ix: 4 },
              sa: { a: 0, k: 0, ix: 5 },
              nm: '변형',
            },
          ],
          nm: '그룹 1',
          np: 2,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: 'ADBE Vector Group',
          hd: false,
        },
      ],
      ip: 0,
      op: 2100,
      st: 0,
      bm: 0,
    },
  ],
  markers: [],
};
